import { useRef, useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import { eventMetaFor } from '../eventMeta';
import { NotificationsPanel } from './NotificationsPanel';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * The header bell.
 *
 * Four things here are deliberate and easy to "tidy" back into bugs. Each cost
 * a real defect, and none of them can be caught by reading the screen once.
 *
 *   1. EVERY COLOUR IS A TOKEN. Do not reintroduce a literal. The row title
 *      was `text-slate-900` on the tokenised `bg-popover`, which turns #1e1b31
 *      in dark mode — 1.07:1, invisible. No test in this repo can see that.
 *
 *   2. THE HOVER TINT WAS MEASURED, against the row's DESCRIPTION text: it is
 *      `text-muted-foreground` and so the first thing to fail. The Figma's
 *      `hover:bg-accent/50` gives 4.01:1 light / 3.87:1 dark and
 *      `hover:bg-muted` 4.34:1 light — both under 4.5:1 — so hover is
 *      `bg-muted/50` (4.55:1 / 6.08:1). Unread is a DOT rather than a
 *      background tint for the same reason: `bg-primary/5` behind a row
 *      measures 4.42:1. Highlighting a row by tinting it is what breaks it.
 *
 *   3. THE FOCUS INDICATOR IS A REAL `outline`. Not a ring (a box-shadow) and
 *      not a tint (a background) — forced-colors mode overrides both. This
 *      shipped once as `focus-visible:outline-none` with only
 *      `focus-visible:bg-muted/50` in its place, which measures 1.05:1 against
 *      the row: the rows this panel exists to make keyboard-operable had no
 *      visible focus at all, and a focused row was pixel-identical to a merely
 *      hovered one. `outline-ring` measures 5.70:1 / 3.95:1 on bg-popover.
 *      Routing these through `Button` does not fix it — its `ring-ring/50
 *      ring-1` measures 2.26:1 / 1.95:1 and fails too.
 *
 *   4. ROWS CARRY NO `aria-label`. Omitted on purpose: an `aria-label` on a
 *      container REPLACES its inner text for a screen reader, which is exactly
 *      the bug the bell trigger had — a hardcoded `aria-label="Notifications"`
 *      made the unread badge unannounceable, so the button read identically at
 *      0 and at 9+. Unread is carried by an `sr-only` word as well as the dot,
 *      so it never depends on seeing a colour.
 *
 * Clicking a row opens `NotificationsPanel` on that item; the footer opens the
 * same panel on the newest. The popover closes as the panel opens, so the two
 * overlays never nest — which is also why the panel needs an explicit
 * focus-return to the bell (see `bellRef`).
 */
export const HeaderNotifications = () => {
  const { t } = useTranslation('app-shell');

  const {
    notifications,
    unreadCount,
    isLoading,
    isError,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [focusId, setFocusId] = useState<string | undefined>(undefined);
  // Radix returns focus to a Dialog's own DialogTrigger on close. This dialog
  // has none — it opens from a row inside a popover that is gone by then — so
  // its triggerRef is null and focus lands on <body>, dropping a keyboard user
  // at the top of the document. The bell is where they were.
  const bellRef = useRef<HTMLButtonElement>(null);

  const openDialog = (id?: string) => {
    if (id !== undefined) setFocusId(id);
    else setFocusId(undefined);
    setDialogOpen(true);
    setPopoverOpen(false);
  };

  const openRow = (id: string, isRead: boolean) => {
    // Reading it is what marks it read, so only unread rows write.
    if (!isRead) markAsRead(id);
    openDialog(id);
  };

  return (
    <>
      {/*
        Announces the count when it CHANGES. It lives out here, permanently
        mounted, because a live region that mounts together with its own text —
        as anything inside the popover would — is not reliably announced.
      */}
      <span className="sr-only" role="status" aria-live="polite">
        {unreadCount > 0
          ? t('header.notifications.aria_label', { count: unreadCount })
          : ''}
      </span>

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            ref={bellRef}
            /* `bg-card`, not `bg-white`: identical in light mode (--card is
               #ffffff there) and #1e1b31 in dark, where a hard white pill would
               glow. The AI button beside this one still writes `bg-white
               text-slate-600` literally, though its own comment says the intent
               was muted-foreground — so it carries the same latent dark-mode
               bug. Out of scope here, but that is why the two files differ. */
            className="relative h-9 w-9 rounded-md border border-border/60 bg-card p-0 transition-colors hover:bg-muted/50"
            aria-label={
              unreadCount > 0
                ? t('header.notifications.aria_label', { count: unreadCount })
                : t('header.notifications.aria_label_none')
            }
          >
            {/* Size and stroke from staging (`h-5 w-5`, `strokeWidth={1.75}`);
                the COLOUR is a token rather than staging's literal `#121212`,
                which is a near-black that disappears on `--card` in dark mode.
                Same intent — a darker, more present bell than the muted grey
                this replaced — expressed so it survives the theme. */}
            <Bell
              className="h-5 w-5 text-foreground"
              strokeWidth={1.75}
              aria-hidden="true"
            />

            {unreadCount > 0 && (
              <Badge
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-destructive p-0 text-xs hover:bg-destructive"
                aria-hidden="true"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-80 p-0" align="end">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h4 className="text-sm font-semibold">
              {t('header.notifications.title')}
            </h4>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {unreadCount} {t('header.notifications.new')}
              </Badge>
            )}
          </div>

          {isLoading && (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            </div>
          )}

          {/*
            Error before empty, and never both: "No new notifications" on a
            failed read is a claim about the firm rather than about the request.

            AND only when there is nothing cached. react-query keeps `data`
            when a REFETCH fails, so gating on `isError` alone blanked a panel
            holding twelve perfectly good notifications — and the dialog opened
            on top of it — the moment a background poll failed.
          */}
          {!isLoading && isError && notifications.length === 0 && (
            <div className="flex h-32 items-center justify-center px-4">
              <p className="text-center text-xs text-muted-foreground">
                {t('header.notifications.error')}
              </p>
            </div>
          )}

          {!isLoading && !isError && notifications.length === 0 && (
            <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
              <Bell className="mb-2 h-8 w-8 opacity-20" aria-hidden="true" />
              <p className="text-xs">{t('header.notifications.empty')}</p>
            </div>
          )}

          {notifications.length > 0 && (
            /* The scroll container is the list itself, so the scrollable
               region is the thing that carries the list semantics. */
            <ul className="max-h-60 overflow-y-auto">
              {notifications.map((notif) => {
                const { Icon, className } = eventMetaFor(
                  notif.eventType,
                  notif.type,
                );
                return (
                  <li key={notif.id} className="border-b last:border-b-0">
                    <button
                      type="button"
                      onClick={() => openRow(notif.id, notif.isRead)}
                      className="flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                    >
                      <Icon
                        className={cn('mt-0.5 h-4 w-4 shrink-0', className)}
                        aria-hidden="true"
                      />

                      <span className="min-w-0 flex-1 space-y-1">
                        <span className="flex items-center gap-1.5">
                          <span className="text-sm font-medium">
                            {/* A colon, and it lives INSIDE the string.
                                The accessible name concatenates adjacent
                                inline text nodes without putting anything
                                between them, and a separate `{' '}` node is
                                dropped outright — so the previous markup
                                computed as "UnreadClient commented on your
                                answers", one run-on token. Punctuation in the
                                same node is the only part nothing can eat. */}
                            {!notif.isRead && (
                              <span className="sr-only">
                                {`${t('header.notifications.unread')}: `}
                              </span>
                            )}
                            {notif.title}
                          </span>
                          {/* The Figma's unread dot. Sits AFTER the title
                              rather than in its own left column: read and
                              unread rows then share one text origin, so
                              nothing shifts sideways as items are read. */}
                          {!notif.isRead && (
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                        <span className="text-xs leading-relaxed text-muted-foreground line-clamp-2">
                          {notif.description}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {notif.timestamp}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t p-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs hover:bg-muted/50"
              onClick={() => openDialog()}
            >
              {t('header.notifications.view_all')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <NotificationsPanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        notifications={notifications}
        isLoading={isLoading}
        isError={isError}
        focusId={focusId}
        // Selecting a message inside the panel marks it read too, not just
        // clicking a row out here. Without this, walking the whole inbox from
        // the panel left the bell's badge exactly where it started.
        onRead={markAsRead}
        onReadAll={markAllAsRead}
        returnFocusTo={bellRef}
      />
    </>
  );
};
